import { apiClient } from '@/shared/services/client'
import type {
  ConfiguracionRegistros,
  PaginatedConfiguraciones,
  MarcadorEspecial,
  AgregacionResponse,
  AgregacionBarrasResponse,
  TipoAgregacion,
  DashboardAsistenciasResponse,
} from '../types'

const BASE_URL = '/registros'

export const registrosApi = {
  // Configuraciones
  getConfiguraciones: async (params?: {
    page?: number
    page_size?: number
    search?: string
    activo?: boolean
    grupo_id?: number
  }): Promise<PaginatedConfiguraciones> => {
    const { data } = await apiClient.get(`${BASE_URL}/configuraciones/`, { params })
    return data
  },

  getConfiguracion: async (id: number): Promise<ConfiguracionRegistros> => {
    const { data } = await apiClient.get(`${BASE_URL}/configuracion/${id}/`)
    return data
  },

  createConfiguracion: async (payload: Partial<ConfiguracionRegistros>) => {
    const { data } = await apiClient.post(`${BASE_URL}/configuracion/`, payload)
    return data
  },

  updateConfiguracion: async (id: number, payload: Partial<ConfiguracionRegistros>) => {
    const { data } = await apiClient.put(`${BASE_URL}/configuracion/${id}/`, payload)
    return data
  },

  deleteConfiguracion: async (id: number) => {
    const { data } = await apiClient.delete(`${BASE_URL}/configuracion/${id}/`)
    return data
  },

  toggleActivo: async (id: number) => {
    const { data } = await apiClient.post(`${BASE_URL}/configuracion/${id}/toggle-activo/`)
    return data
  },

  // Grupos
  getGrupos: async () => {
    const { data } = await apiClient.get('/group-extensions/all/')
    return data
  },

  testConexion: async (payload: {
    db_host: string
    db_port: string
    db_name: string
    db_user: string
    db_password?: string
    db_engine: string
    tabla_nombre?: string
    config_id?: number  // ID de configuración existente para usar contraseña almacenada
  }) => {
    const { data } = await apiClient.post(`${BASE_URL}/configuracion/test-conexion/`, payload)
    return data
  },

  // Endpoints de Reporte
  getEstadisticas: async (configId: number, filtros?: Record<string, string>) => {
    const params = filtros
      ? Object.entries(filtros).reduce(
          (acc, [key, value]) => ({ ...acc, [`filtro_${key}`]: value }),
          {}
        )
      : {}
    const { data } = await apiClient.get(`${BASE_URL}/reporte/${configId}/estadisticas/`, {
      params,
    })
    return data
  },

  getDatosGraficos: async (configId: number, filtros?: Record<string, string>) => {
    const params = filtros
      ? Object.entries(filtros).reduce(
          (acc, [key, value]) => ({ ...acc, [`filtro_${key}`]: value }),
          {}
        )
      : {}
    const { data } = await apiClient.get(`${BASE_URL}/reporte/${configId}/graficos/`, { params })
    return data
  },

  getInsights: async (configId: number, filtros?: Record<string, string>) => {
    const params = filtros
      ? Object.entries(filtros).reduce(
          (acc, [key, value]) => ({ ...acc, [`filtro_${key}`]: value }),
          {}
        )
      : {}
    const { data } = await apiClient.get(`${BASE_URL}/reporte/${configId}/insights/`, { params })
    return data
  },

  getRegistros: async (
    configId: number,
    params?: {
      page?: number
      page_size?: number
      ordenar_por?: string
      filtros?: Record<string, string>
      busqueda?: string
    }
  ) => {
    const filtrosConPrefijo = params?.filtros
      ? Object.entries(params.filtros).reduce(
          (acc, [key, value]) => ({ ...acc, [`filtro_${key}`]: value }),
          {} as Record<string, string>
        )
      : {}
    const queryParams = {
      page: params?.page || 1,
      page_size: params?.page_size || 25,
      ordenar_por: params?.ordenar_por,
      busqueda: params?.busqueda,
      ...filtrosConPrefijo,
    }
    const { data } = await apiClient.get(`${BASE_URL}/reporte/${configId}/datos/`, {
      params: queryParams,
    })
    return data
  },

  getValoresFiltro: async (configId: number, campo: string) => {
    const { data } = await apiClient.get(
      `${BASE_URL}/reporte/${configId}/valores-filtro/${campo}/`
    )
    return data
  },

  exportarRegistros: async (
    configId: number,
    formato: 'csv' | 'excel',
    filtros?: Record<string, string>
  ) => {
    const params = {
      formato,
      ...filtros,
    }
    const response = await apiClient.get(`${BASE_URL}/reporte/${configId}/exportar/`, {
      params,
      responseType: 'blob',
    })
    return response.data
  },

  // Marcadores Especiales (Hitos) - usando API v2 Django Ninja
  getMarcadores: async (configId: number): Promise<{ marcadores: MarcadorEspecial[]; total: number }> => {
    const { data } = await apiClient.get(`${BASE_URL}/marcadores/`, {
      params: { configuracion_id: configId, page_size: 100 }
    })
    // Transformar respuesta paginada a formato esperado
    return {
      marcadores: data.results || [],
      total: data.count || 0
    }
  },

  createMarcador: async (payload: {
    configuracion_id: number
    fecha: string
    titulo: string
    descripcion?: string
    url_reporte?: string
    tipo: string
    color: string
    activo: boolean
    ultimo_hito: boolean
    primer_hito: boolean
  }): Promise<MarcadorEspecial> => {
    const { data } = await apiClient.post(`${BASE_URL}/marcador/`, payload)
    return data
  },

  updateMarcador: async (
    marcadorId: number,
    payload: Partial<{
      fecha: string
      titulo: string
      descripcion: string
      url_reporte: string
      tipo: string
      color: string
      activo: boolean
      ultimo_hito: boolean
      primer_hito: boolean
    }>
  ): Promise<MarcadorEspecial> => {
    const { data } = await apiClient.put(`${BASE_URL}/marcador/${marcadorId}/`, payload)
    return data
  },

  deleteMarcador: async (marcadorId: number): Promise<{ success: boolean }> => {
    const { data } = await apiClient.delete(`${BASE_URL}/marcador/${marcadorId}/`)
    return data
  },

  // Agregación para visualización dinámica
  getAgregacion: async (
    configId: number,
    columna: string,
    tipo: TipoAgregacion = 'COUNT',
    valor?: string
  ): Promise<AgregacionResponse> => {
    const params: Record<string, string> = { columna, tipo }
    if (valor) params.valor = valor
    const { data } = await apiClient.get(`${BASE_URL}/reporte/${configId}/agregacion/`, {
      params,
    })
    return data
  },

  getAgregacionBarras: async (
    configId: number,
    columna: string,
    limite: number = 10
  ): Promise<AgregacionBarrasResponse> => {
    const { data } = await apiClient.get(`${BASE_URL}/reporte/${configId}/agregacion-barras/`, {
      params: { columna, limite },
    })
    return data
  },

  getDashboardAsistencias: async (configId: number): Promise<DashboardAsistenciasResponse> => {
    const { data } = await apiClient.get(`${BASE_URL}/reporte/${configId}/asistencias/`)
    return data
  },
}
